import { SectionShell } from "@/components/design-system/SectionShell";
import { Textarea } from "@/components/ui/textarea";

const code = `<div className="space-y-2">
  <label htmlFor="notes">Notes</label>
  <Textarea id="notes" rows={4} placeholder="Anything worth remembering…" />
  <p className="text-xs text-ink-muted">Markdown is not supported here.</p>
</div>

{/* Machine text — SQL, code, config */}
<Textarea mono rows={10} defaultValue="SELECT id FROM members" />`;

export function TextareaSection() {
  return (
    <SectionShell
      id="textarea"
      title="Textarea"
      description={
        <>
          A multi-line text field. Shares the <code>.form-control</code> look of{" "}
          <code>Input</code> and <code>Select</code> so a form reads as one set,
          and grows from a comfortable minimum height rather than a fixed one.
          Pass <code>mono</code> for machine text.
        </>
      }
      whenToUse={
        <ul>
          <li>Free-form prose that runs past a single line — descriptions, notes, message bodies.</li>
          <li>Machine text such as SQL, code, or configuration, with <code>mono</code>.</li>
          <li>Anywhere a character limit needs a visible counter beneath the field.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>A single short value — use <code>Input</code>.</li>
          <li>A fixed set of choices — use <code>Select</code> or <code>Combobox</code>.</li>
        </ul>
      }
      preview={
        <div className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="ds-textarea">Notes</label>
            <Textarea
              id="ds-textarea"
              rows={4}
              placeholder="Anything worth remembering…"
            />
            <p className="text-xs text-ink-muted">Markdown is not supported here.</p>
          </div>

          <div className="space-y-2">
            <label htmlFor="ds-textarea-mono">Query</label>
            <Textarea
              id="ds-textarea-mono"
              mono
              rows={4}
              defaultValue={"SELECT id, first_name\nFROM members\nWHERE status = 'Active'"}
            />
            <p className="text-xs text-ink-muted">
              <code>mono</code> switches to the monospace stack and turns spellcheck off.
            </p>
          </div>
        </div>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>rows</code> sets the starting height; the field never shrinks
            below <code>min-h-[6rem]</code> from <code>.form-control-textarea</code>.
          </li>
          <li>
            <code>mono</code> applies the monospace stack, a smaller size, and{" "}
            <code>spellCheck=false</code>. An explicit <code>spellCheck</code> prop still wins.
          </li>
          <li>
            For a character limit, put the counter in the helper-text slot and
            flip it to <code>text-danger-display</code> once it is exceeded.
          </li>
          <li>
            Error state matches the rest of the form: <code>aria-invalid</code> on the
            field, message in <code>text-xs text-danger-display</code>.
          </li>
        </ul>
      }
    />
  );
}
